import { Tooltip, Button, testIdGen } from '@panwds/react-ui';
import { classNames } from '@panwds/tailwindcss';
import { ParentSize } from '@visx/responsive';
import React, { useRef, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFilters } from '../../hooks/useFilters/useFilters.esm.js';
import { actions } from '../../state/actions.esm.js';
import '../../state/reducer.esm.js';
import { FilterProvider } from '../FilterProvider/FilterProvider.esm.js';
import { FilterPills } from './FilterPills.esm.js';
import { useHashParams } from './useHashParams.esm.js';

const defaultConfig = {
    deserialize: (value) => value,
    serialize: (value) => value,
    static: false
};
const FilterBarWithSize = ({ actions: actionButtons, dataMetrics = 'filter-bar', dataTestId, deserialize, serialize, disabled = false, defaultState, enableQueryParams = false, filters: filterConfig, isLoading = false, name, parentWidth, saveOnUnmount = true, background = '', showResetBtn = true, onResetClicked, onFilterTypeAdded, onFilterTypeRemoved, hideAddFilter = false, addFilterLabel, showLessLabel = 'Show Less', resetFiltersLabel = 'Reset Filters', resetLabel, reinitialize = false, hideSelectAll = false }) => {
    var _a, _b;
    const intl = useIntl();
    const actionsRef = useRef(null);
    const { dispatch, displayedFilters, resetFilters } = useFilters(name);
    const normalizedFilterConfig = useMemo(() => filterConfig.reduce((currentFilters, filter) => {
        currentFilters[filter.name] = {
            ...defaultConfig,
            ...filter,
            isDisplayed: displayedFilters.includes(filter.name)
        };
        return currentFilters;
    }, {}), [name, filterConfig, displayedFilters]);
    const params = useHashParams(name, enableQueryParams, normalizedFilterConfig, deserialize, serialize);
    useEffect(() => {
        if (saveOnUnmount)
            return;
        return () => dispatch({
            type: actions.remove,
            payload: {
                name: name
            }
        });
    }, [name]);
    useEffect(() => {
        dispatch({
            type: actions.init,
            payload: {
                name: name,
                defaultState,
                filterConfig: filterConfig,
                initialFilters: params,
                reinitialize
            }
        });
    }, [name, filterConfig]);
    const containerClassname = classNames('tw-flex tw-text-default dark:tw-text-dark-bg tw-p-2', 'hover:tw-text-secondary focus:tw-outline-none focus:tw-ring', background || 'tw-bg-transparent');
    const getResetBtn = (React.createElement(Tooltip, { label: resetFiltersLabel },
        React.createElement(Button, { addClassName: "tw-whitespace-nowrap", "aria-label": "Reset", onClick: () => {
                resetFilters();
                onResetClicked === null || onResetClicked === void 0 ? void 0 : onResetClicked();
            }, disabled: disabled || isLoading, appearance: "tertiary-clear", dataMetrics: `${dataMetrics}-reset-filters` }, resetLabel ||
            intl.formatMessage({
                defaultMessage: 'Reset',
                id: 'QeBCkb',
                description: 'Resets the filters to default state'
            }))));
    return (React.createElement(FilterProvider, { name: name },
        React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || `filter-bar-${testIdGen(name)}`, className: containerClassname },
            React.createElement(FilterPills, { normalizedFilterConfig: normalizedFilterConfig, parentWidth: parentWidth - ((_b = (_a = actionsRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 0), disabled: disabled, isLoading: isLoading, dataMetrics: dataMetrics, onFilterTypeAdded: onFilterTypeAdded, onFilterTypeRemoved: onFilterTypeRemoved, hideAddFilter: hideAddFilter, addFilterLabel: addFilterLabel, showLessLabel: showLessLabel, hideSelectAll: hideSelectAll }),
            React.createElement("div", { className: "tw-flex tw-space-x-1 tw-items-start", ref: actionsRef },
                actionButtons,
                showResetBtn && getResetBtn))));
};
/**
 *
 * The FilterBar is responsible for displaying active filters, and hiding
 * inactive filters.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { FilterBar, GlobalFilterProvider, SelectFilter } from "@panwds/react-filters";
 * ```
 *
 * GlobalFilterProvider should already be set in the Root component of your project,
 * but when writing tests, you'll need to wrap your component with GlobalFilterProvider.
 */
const FilterBar = (props) => {
    return (React.createElement("div", null,
        React.createElement(ParentSize, null, (parent) => React.createElement(FilterBarWithSize, { ...props, parentWidth: parent.width }))));
};

export { FilterBar };
//# sourceMappingURL=FilterBar.esm.js.map
